/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, TextField, Button, Typography, Alert, Link, InputAdornment, IconButton } from "@mui/material";

import { useParams, useNavigate } from "react-router-dom";

import { changePassword } from "./changePasswordFunctions";

import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

const ChangePassword = () => {
    const navigate = useNavigate();
    const { primary, secondary } = useParams();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");

    const [complete, setComplete] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const [revealPassword, setRevealPassword] = useState<boolean>(false);
    const [revealConfirmPassword, setRevealConfirmPassword] = useState<boolean>(false);

    function passwordsMatch() {
        return password !== "" && password === confirmPassword;
    }

    if (error) {
        return (
            <Box sx={{ m: 4 }}>
                <Alert severity="error">
                    This request has expired or is invalid. If you require a password reset, please create one at the login page
                    or contact your system administrator.
                </Alert>
                <Box sx={{ mb: 5 }} />
                <Button
                    fullWidth
                    variant={"contained"}
                    onClick={() => {
                        navigate("/");
                    }}
                >
                    Back to Login
                </Button>
            </Box>
        );
    }

    if (complete) {
        return (
            <Box sx={{ m: 4 }}>
                <Alert severity="success">
                    <Typography>Password reset successful.</Typography>
                </Alert>
                <Box sx={{ mb: 5 }} />
                <Button
                    fullWidth
                    variant={"contained"}
                    onClick={() => {
                        navigate("/");
                    }}
                >
                    Back to Login
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ m: 4 }}>
            <Typography align="center" variant="h6" marginBottom={"20px"}>
                Please enter your new password and confirm it here.
            </Typography>

            <Alert severity={"info"}>
                <Typography>
                    {"This page can only be used one time. Future password resets may be triggered via the "}
                    {<Link href={"/"}>Login Page</Link>}
                    {
                        ". It is recommended that you use a strong password (8+ characters with a mix of upper case and lower case letters, numbers and symbols)."
                    }
                </Typography>
            </Alert>

            <TextField
                onChange={(event) => {
                    setPassword(event.target.value);
                }}
                type={revealPassword ? "text" : "password"}
                margin="normal"
                required
                fullWidth
                id="password"
                label={"New Password"}
                name="password"
                value={password}
                InputProps={{
                    endAdornment: (
                        <InputAdornment disableTypography position="start">
                            <IconButton onClick={() => setRevealPassword(!revealPassword)}>
                                {revealPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <TextField
                onChange={(event) => {
                    setConfirmPassword(event.target.value);
                }}
                type={revealConfirmPassword ? "text" : "password"}
                margin="normal"
                required
                fullWidth
                id="password"
                label={"Confirm New Password"}
                name="password"
                value={confirmPassword}
                InputProps={{
                    endAdornment: (
                        <InputAdornment disableTypography position="start">
                            <IconButton onClick={() => setRevealConfirmPassword(!revealConfirmPassword)}>
                                {revealConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Button
                fullWidth
                disabled={!passwordsMatch()}
                variant={"contained"}
                onClick={() =>
                    changePassword(
                        primary as string,
                        secondary as string,
                        password,
                        dispatch,
                        () => setComplete(true),
                        () => setError(true)
                    )
                }
            >
                Submit
            </Button>
        </Box>
    );
};

export default ChangePassword;
